import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ErrorMessages, type Question } from '../../config/types';
import {
  chainValidations,
  checkAllFieldsHaveValues,
  groupValidation,
  objectToString,
  stringToObject,
} from '../../utils/form-utils';
import GradeInput from './DateInputField/GradeInput';
import GradeInputColumn from './DateInputField/GradeInputColumn';
import { GradeInputWrapper } from './DateInputField/GradeInputField';
import {
  dateIsEmpty,
  dateIsFullyPopulated,
  getMinYearValue,
  invalidDateErrorMessage,
  isMinYearRuleValid,
  isValidDate,
} from './DateInputField/utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';

interface GradeInputProps {
  name: string;
  defaultValue?: string;
  disabled?: boolean;
  anchorId?: string;
  onChange?(value: string): void;
}

export const getColumns = () => ['PositionAdvertised', 'TotalApplications'];
export const getRows = () => ['scs2', 'scs1', 'grade6', 'grade7', 'seo', 'heo', 'eo', 'ao', 'aa'];
export const getKeys = () =>
  getRows().flatMap((row) => getColumns().map((column) => `${row}${column}`));

export function GradeInputWrapperColumn({
  name,
  defaultValue,
  disabled = false,
  anchorId = useId(),
  onChange,
}: GradeInputProps) {
  const generateInputId = (suffix: string) => `${anchorId}-${suffix}`;

  const initialValues = useMemo(() => {
    return stringToObject(defaultValue);
  }, [defaultValue]);

  const [values, setValues] = useState(initialValues);

  const triggerChange = (updatedValues: typeof values) => {
    if (onChange) {
      const valueString = objectToString(updatedValues);
      if (dateIsEmpty(valueString)) {
        onChange('');
      } else {
        onChange(valueString);
      }
    }
  };
  // Type the field argument to only accept valid keys of 'values'
  const handleValueChange = (field: keyof typeof values) => (newValue: string) => {
    const updatedValues = { ...values, [field]: newValue };
    setValues(updatedValues);
    triggerChange(updatedValues);
  };

  const gradeRows = useMemo(
    () => [
      { grade: 'SCS 2', fields: ['scs2PositionAdvertised', 'scs2TotalApplications'] },
      { grade: 'SCS 1', fields: ['scs1PositionAdvertised', 'scs1TotalApplications'] },
      { grade: 'Grade 6', fields: ['grade6PositionAdvertised', 'grade6TotalApplications'] },
      { grade: 'Grade 7', fields: ['grade7PositionAdvertised', 'grade7TotalApplications'] },
      { grade: 'SEO', fields: ['seoPositionAdvertised', 'seoTotalApplications'] },
      { grade: 'HEO', fields: ['heoPositionAdvertised', 'heoTotalApplications'] },
      { grade: 'EO', fields: ['eoPositionAdvertised', 'eoTotalApplications'] },
      { grade: 'AO', fields: ['aoPositionAdvertised', 'aoTotalApplications'] },
      { grade: 'AA', fields: ['aaPositionAdvertised', 'aaTotalApplications'] },
    ],
    [],
  );

  return (
    <div>
      <table className="govuk-table">
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header">
              Grade
            </th>
            <th scope="col" className="govuk-table__header">
              Total number of posts that were advertised in these campaigns
            </th>
            <th scope="col" className="govuk-table__header">
              Total number of applicants to these campaigns
            </th>
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {gradeRows.map(({ grade, fields }) => (
            <tr key={grade} className="govuk-table__row">
              <th scope="row" className="govuk-table__header">
                {grade}
              </th>
              {fields.map((field) => (
                <td key={field} className="govuk-table__cell">
                  <GradeInputColumn
                    testId={`${field}-${grade.toLowerCase().replace(' ', '-')}`}
                    disabled={disabled}
                    anchorId={generateInputId(field)}
                    onChange={handleValueChange(field as keyof typeof values)} // Cast field to a valid key
                    value={values[field as keyof typeof values]} // Cast field to a valid key
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface GradeInputFieldProps {
  question: Question;
  disabled?: boolean;
  anchorId: string;
  detailsHeading?: string;
  detailsContent?: string;
}

export function InputRecruitmentCampaigns({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: GradeInputFieldProps) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);

  useEffect(() => {
    if (getValues(question.name) === undefined) {
      setValue(question.name, '');
    }
  }, [getValues, question.name, setValue]);

  const { onChange, name } = register(question.name, {
    validate: (value: string) =>
      chainValidations([
        checkAllFieldsHaveValues({
          value: stringToObject(value),
          keys: getKeys(),
        }),
      ]),
  });

  const handleChange = (dateChange: string) => {
    onChange({ target: { name, value: dateChange } });
  };

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}
      <div className="govuk-date-input">
        <GradeInputWrapperColumn
          name={question.name}
          disabled={disabled}
          anchorId={anchorId}
          onChange={handleChange}
          defaultValue={getValues(question.name)}
        />
      </div>
    </>
  );
}
